import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Check, ChevronRight, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookIllustration = () => (
  <svg viewBox="0 0 120 100" className="w-20 h-20 mx-auto">
    <g transform="translate(20, 10)">
      {/* Book back cover */}
      <rect x="15" y="5" width="55" height="70" rx="3" fill="#e8d5c4" stroke="#c4a882" strokeWidth="1.5" transform="rotate(-5, 42, 40)" />
      {/* Book pages */}
      <rect x="18" y="8" width="50" height="65" rx="2" fill="#fff" stroke="#ddd" strokeWidth="1" transform="rotate(-3, 43, 40)" />
      {/* Book front cover */}
      <rect x="20" y="10" width="50" height="65" rx="3" fill="#e74c3c" stroke="#c0392b" strokeWidth="1.5" />
      {/* Page lines on cover */}
      <rect x="30" y="25" width="30" height="3" rx="1" fill="#fff" opacity="0.7" />
      <rect x="30" y="32" width="25" height="3" rx="1" fill="#fff" opacity="0.5" />
      <rect x="30" y="39" width="20" height="3" rx="1" fill="#fff" opacity="0.3" />
      {/* Bookmark */}
      <polygon points="55,10 55,30 60,25 65,30 65,10" fill="#e67e22" />
    </g>
  </svg>
);

const ParcoursCard = ({ parcours, onDelete }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`parcours-card-${parcours.parcours_id}`}>
    <div className="bg-violet-500 p-6 flex items-center justify-center h-36">
      <BookIllustration />
    </div>
    <div className="p-4 space-y-3">
      <h3 className="font-bold text-sm text-slate-900 line-clamp-2 min-h-[2.5rem]">{parcours.name}</h3>
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <Check className="h-3.5 w-3.5 text-emerald-500" />
        <span>{parcours.sequence_ids?.length || 0} séquence{(parcours.sequence_ids?.length || 0) !== 1 ? 's' : ''}</span>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full text-slate-600 hover:text-red-600 hover:border-red-200"
        onClick={() => onDelete(parcours.parcours_id)}
        data-testid={`delete-parcours-${parcours.parcours_id}`}
      >
        <Trash2 className="h-3.5 w-3.5 mr-1.5" />Supprimer
      </Button>
    </div>
  </div>
);

const ElearningParcoursPage = () => {
  const navigate = useNavigate();
  const [parcours, setParcours] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchParcours = async () => {
    try {
      // Fetch parcours from all sessions
      const sessRes = await fetch(`${API}/admin/sessions`, { credentials: 'include' });
      if (!sessRes.ok) return;
      const sessions = await sessRes.json();
      const allParcours = [];
      for (const sess of sessions) {
        const pRes = await fetch(`${API}/admin/sessions/${sess.session_id}/elearning/parcours`, { credentials: 'include' });
        if (pRes.ok) {
          const data = await pRes.json();
          allParcours.push(...data.map(p => ({ ...p, session_title: sess.title })));
        }
      }
      setParcours(allParcours);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchParcours(); }, []);

  const handleDelete = async (parcoursId) => {
    if (!window.confirm('Supprimer ce parcours ?')) return;
    const p = parcours.find(x => x.parcours_id === parcoursId);
    if (!p) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${p.session_id}/elearning/parcours/${parcoursId}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) {
        setParcours(prev => prev.filter(x => x.parcours_id !== parcoursId));
        toast.success('Parcours supprimé');
      }
    } catch (e) { toast.error('Erreur lors de la suppression'); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="elearning-parcours-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>LMS</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-violet-600 font-medium">Parcours</span>
        </div>

        <h1 className="text-3xl font-black text-slate-900">Mes parcours e-learning</h1>

        <Button
          className="bg-violet-500 hover:bg-violet-600 text-white rounded-lg gap-2"
          onClick={() => toast.info('Créez un parcours depuis une session de formation')}
          data-testid="btn-create-parcours"
        >
          <Plus className="h-4 w-4" />Créer un parcours
        </Button>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-60 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : parcours.length === 0 ? (
          <div className="border border-slate-200 rounded-xl p-12 text-center">
            <BookOpen className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun parcours</h3>
            <p className="text-sm text-slate-500">Créez votre premier parcours e-learning depuis une session de formation.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {parcours.map(p => (
              <ParcoursCard key={p.parcours_id} parcours={p} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ElearningParcoursPage;
